import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Button } from '../../../../shared/components/button/Button';
import { formatCurrency } from '../../../../shared/utils/helpers/numbers';

export interface ItemParaDevolver {
  nfeIt_id: number;
  numero_nota: number;
  serie?: string;
  fornecedor?: string;
  produto?: string;
  codigo_promatec?: string;
  qtd_na_nota: number;
  qtd_devolvida: number;
  saldo: number;
  valor_unitario: number;
  dias: number;
  alerta_prazo?: string;
}

interface Props {
  aberto: boolean;
  item: ItemParaDevolver | null;
  salvando: boolean;
  aoFechar: () => void;
  aoConfirmar: (quantidade: number, motivo: string) => void;
}

/**
 * Tela 2 — criar o registro de devolução.
 *
 * Diálogo, e não rota nova: o operador chega aqui de dentro da consulta, com a linha já
 * escolhida. Uma rota nova obrigaria a procurar o mesmo item uma segunda vez.
 *
 * A confirmação diz o IMPACTO CONCRETO, não "deseja continuar?": produto, quantidade,
 * nota de origem e o saldo que vai restar. É o padrão de confirmação que o card define
 * para todas as ações de ida e volta.
 */
export const ModalCriarDevolucao = ({ aberto, item, salvando, aoFechar, aoConfirmar }: Props) => {
  const [quantidade, setQuantidade] = useState<string>('');
  const [motivo, setMotivo] = useState<string>('');

  useEffect(() => {
    if (aberto) {
      setQuantidade('');
      setMotivo('');
    }
  }, [aberto, item?.nfeIt_id]);

  if (!item) return null;

  const qtd = Number(String(quantidade).replace(',', '.'));
  const qtdValida = !Number.isNaN(qtd) && qtd > 0 && qtd <= item.saldo;
  const saldoRestante = qtdValida ? item.saldo - qtd : item.saldo;

  /* A mensagem de erro é específica, e não "valor inválido": o operador precisa saber se
     errou para mais ou se simplesmente não digitou nada. */
  const erroQuantidade =
    quantidade === ''
      ? null
      : Number.isNaN(qtd) || qtd <= 0
      ? 'Informe uma quantidade maior que zero.'
      : qtd > item.saldo
      ? `O saldo devolvível deste item é ${item.saldo}.`
      : null;

  return (
    <Dialog open={aberto} onClose={aoFechar} maxWidth="sm" fullWidth>
      <DialogTitle>Criar devolução ao fornecedor</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {item.codigo_promatec} — {item.produto}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nota {item.numero_nota}
            {item.serie ? ` / série ${String(item.serie).trim()}` : ''} · {item.fornecedor}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            Quantidade na nota: <strong>{item.qtd_na_nota}</strong>
          </Typography>
          <Typography variant="body2">
            Já devolvida: <strong>{item.qtd_devolvida}</strong>
          </Typography>
          <Typography variant="body2">
            Saldo devolvível: <strong>{item.saldo}</strong>
          </Typography>
          <Typography variant="body2">
            Valor unitário: <strong>{formatCurrency(item.valor_unitario)}</strong>
          </Typography>
        </Box>

        {/* D11: o prazo é informação, nunca trava. O alerta explica o que está em jogo —
            o crédito do imposto — para o operador decidir com o que importa à vista. */}
        {item.alerta_prazo === 'S' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta nota foi emitida há {item.dias} dias. O prazo do art. 452 do RICMS/SP é de
            45 dias e é condição para o crédito do imposto — não impede emitir a devolução.
          </Alert>
        )}

        <TextField
          label="Quantidade a devolver"
          value={quantidade}
          onChange={(e) => setQuantidade(e.target.value)}
          error={Boolean(erroQuantidade)}
          helperText={erroQuantidade ?? ' '}
          autoFocus
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label="Motivo (opcional)"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          /* Confirmado pela Promatec em 19/10: o motivo NÃO sai na nota. Dizer isso aqui
             evita que alguém escreva para o fornecedor ler. */
          helperText="Uso interno: não sai na nota fiscal."
        />

        {qtdValida && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Serão devolvidas <strong>{qtd}</strong> unidade(s) de {item.produto}, da nota{' '}
            {item.numero_nota}. O saldo devolvível deste item passará a ser{' '}
            <strong>{saldoRestante}</strong>.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button className="" variant="secondary" onClick={aoFechar} title="Cancelar" />
        <Button
          className=""
          variant="success"
          disabled={!qtdValida || salvando}
          onClick={() => aoConfirmar(qtd, motivo)}
          title={salvando ? 'Salvando...' : 'Confirmar'}
        />
      </DialogActions>
    </Dialog>
  );
};
